import React, { useCallback, useEffect, useRef, useState } from "react";
import DataTable from "../../../Components/Common/datatable/DataTable";
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Typography,
  Breadcrumbs,
  Tooltip,
  Skeleton,
  TextField,
} from "@mui/material";
import { Link } from "react-router-dom";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import VisibilitySharpIcon from "@mui/icons-material/VisibilitySharp";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import Modal from "../../../Components/Common/Modal/Modal";
import {
  DateFormatter,
  Timestamp,
} from "../../../Components/Common/DateFormatter";
import ApiService from "../../../utils/fetch";
import { useDispatch, useSelector } from "react-redux";
import StatusChip from "./StatusChip";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoPopover from "../../../Components/Common/poper/InfoPopover";
import { MessagePopup } from "../../../Components/Common/messagePopup/MessagePopup";
import { triggerRefresh } from "../../../redux/refresh";
import { useSupport } from "../../../context/SupportContext";

const useDynamicHeight = (rows, paginationModalPageSize, defaultHeight) => {
  const [height, setHeight] = useState(defaultHeight);

  useEffect(() => {
    const rowHeight = 25;
    const headerHeight = 32;
    const computedHeight = rows.length * rowHeight + headerHeight + 200;

    if (rows.length <= paginationModalPageSize) {
      setHeight(computedHeight);
    } else {
      setHeight(defaultHeight);
    }
  }, [rows, paginationModalPageSize, defaultHeight]);

  return height;
};

function PastForeCastTracker() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  let { setPopupCombinationDetails, setCard2CombinationDetails } = useSupport();
  const [modalLoading, setModalLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const [showFailedOnly, setShowFailedOnly] = useState(false);
  const [showSuccessOnly, setShowSuccessOnly] = useState(false);

  const [selectedRowData, setSelectedRowData] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [modalRowData, setModalRowData] = useState([]);

  const GridRef = useRef(null);
  const modalGridRef = useRef(null);

  const finalTreeData = useSelector((state) => state.entity.finalTreeData);

  const refreshKey = useSelector((state) => state.refresh.refreshKey);

  const dispatch = useDispatch();

  useEffect(() => {
    if (finalTreeData.length > 0) {
      const postData = async () => {
        try {
          setLoading(true); // Start loading
          const payload = {
            dashboard: true,
            locationCustomerDTOList: finalTreeData,
          };
          const response = await ApiService.post(
            "/v1/fetch-customer-history-details",
            payload,
          );
          setFilteredData(response.data);
        } catch (error) {
          console.error("Error posting data", error);
        } finally {
          setLoading(false); // Stop loading
        }
      };
      postData();
    } else {
      setFilteredData([]);
      setLoading(false); // Stop loading if no data
    }
  }, [finalTreeData]);

  useEffect(() => {
    const loadPastForecastHistory = async () => {
      setLoading(true);
      try {
        const payload = {
          dashboard: true,
          locationCustomerDTOList: finalTreeData,
        };
        const response = await ApiService.post(
          "/v1/fetch-customer-history-details",
          payload,
        );
        setFilteredData(response.data);
      } catch (error) {
        console.error("Error loading past forecast history", error);
      } finally {
        setLoading(false);
      }
    };

    if (finalTreeData.length > 0) {
      loadPastForecastHistory();
    } else {
      setFilteredData([]);
      setLoading(false);
    }
  }, [refreshKey, finalTreeData]);

  const onModalGridExportCSV = useCallback(() => {
    if (modalGridRef.current && modalGridRef.current.api) {
      const forecastType = selectedRowData?.forecastType || "";

      modalGridRef.current.api.exportDataAsCsv({
        fileName: `${forecastType}_${new Date().toISOString()}.csv`, // Append forecastType before the date
        processHeaderCallback: (params) => {
          // Include hidden columns in the header
          const column = modalDefinition.find(
            (col) => col.field === params.column.getColId(),
          );
          return column ? column.headerName : params.column.getColId();
        },
        processCellCallback: (params) => {
          if (params.column.getColId() === "customerName" && params.value) {
            return params.value; // Return the value for the hidden column
          }

          if (params.column.getColId() === "locationName" && params.value) {
            return params.value; // Return the value for the locationName column
          }

          return params.value; // Return other fields as-is
        },
        useValueFormatterForExport: false,
        allColumns: true, // Disable to favor processCellCallback
      });
    } else {
      console.error("modalGridRef or modalGridRef.api is not available");
    }
  }, [modalGridRef, selectedRowData]);

  // useEffect(() => {
  //   if (GridRef.current && GridRef.current.api) {
  //     GridRef.current.api.refreshCells();
  //   }
  // }, [filteredData]);

  const columnDefinition = [
    {
      headerCheckboxSelection: true,
      checkboxSelection: true,
      field: "supportCheckbox",
      headerName: "",
      sortable: false,
      width: 50,
      suppressSizeToFit: true,
    },
    {
      field: "bottler",
      headerName: "Bottler",
      sortable: true,
      unSortIcon: true,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      width: 140,
      cellRenderer: (params) => (
        <Tooltip title={`${params.data.bottler}`} arrow>
          <div>{params.data.bottler}</div>
        </Tooltip>
      ),
    },
    {
      field: "customerName",
      headerName: "Forecast Partner",
      sortable: true,
      unSortIcon: true,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      width: 210,
      cellRenderer: (params) => (
        <Tooltip title={`${params.data.customerName}`} arrow>
          <div>{params.data.customerName}</div>
        </Tooltip>
      ),
    },
    {
      field: "locationName",
      headerName: "Plant",
      sortable: true,
      unSortIcon: true,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      width: 140,
      cellRenderer: (params) => (
        <Tooltip title={`${params.data.locationName}`} arrow>
          <div>{params.data.locationName}</div>
        </Tooltip>
      ),
    },
    {
      field: "fiscalMonth",
      headerName: "Fiscal Period",
      sortable: true,
      unSortIcon: true,
      width: 180,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
    },
    {
      field: "forecastType",
      headerName: "Forecast Term",
      sortable: true,
      unSortIcon: true,
      width: 190,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
    },
    {
      field: "forecastMode",
      headerName: "Source",
      sortable: true,
      unSortIcon: true,
      width: 140,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
    },
    {
      field: "fileStatus",
      headerName: "Upload Status",
      sortable: true,
      unSortIcon: true,
      width: 190,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      cellRenderer: (params) => <StatusChip status={params.value} />,
    },
    {
      field: "createdAt",
      headerName: "Upload Date-Time(UTC)",
      sortable: true,
      sort: "desc",
      filterParams: {
        comparator: function (filterDate, cellValue) {
          if (!cellValue) return -1;

          const cellDate = new Date(cellValue);
          // Strip time and compare only YYYY-MM-DD
          const cellDateOnly = new Date(
            cellDate.getFullYear(),
            cellDate.getMonth(),
            cellDate.getDate(),
          );

          return cellDateOnly.getTime() - filterDate.getTime();
        },
        filterOptions: ["equals", "inRange"],
        defaultOption: "equals",
        buttons: ["apply", "reset"],
      },
      filter: "agDateColumnFilter",
      width: 260,
      valueFormatter: (params) => {
        return Timestamp(params.value);
      },
    },
    {
      field: "createdBy",
      headerName: "Upload By",
      sortable: true,
      unSortIcon: true,
      // hide: true,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
      width: 190,
      minWidth: 190,
      cellRenderer: (params) => (
        <Tooltip title={params.data.createdBy || ""} arrow>
          <div>{params.data.createdBy}</div>
        </Tooltip>
      ),
    },
    {
      field: "noOfLineItems",
      headerName: "Upload Record Count",
      sortable: true,
      filterParams: {
        filterOptions: ["equals", "inRange"],
        defaultOption: "equals",
        buttons: ["apply", "reset"],
      },
      filter: "agNumberColumnFilter",
      unSortIcon: true,
      width: 240,
    },
    {
      headerName: "View",
      sortable: false,
      resizable: false,
      width: 100,
      cellRenderer: (params) => (
        <div
          onClick={() => handleOpen(params.data)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            cursor: "pointer",
          }}
        >
          <VisibilitySharpIcon />
        </div>
      ),
    },
  ];
  const modalDefinition = [
    {
      field: "customerName",
      headerName: "Forecast Partner",
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      width: 240,
      sortable: true,
      hide: true,
      unSortIcon: true,
      cellRenderer: (params) => (
        <Tooltip title={`${params.data.customerName}`} arrow>
          <div>{params.data.customerName}</div>
        </Tooltip>
      ),
    },
    {
      field: "locationName",
      headerName: "Plant",
      sortable: true,
      hide: true,
      unSortIcon: true,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      width: 240,
      cellRenderer: (params) => (
        <Tooltip title={`${params.data.locationName}`} arrow>
          <div>{params.data.locationName}</div>
        </Tooltip>
      ),
    },
    {
      field: "productId",
      headerName: "Product",
      sortable: true,
      unSortIcon: true,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      width: 260,
      valueGetter: (params) => {
        return `${params.data.productId || ""} - ${
          params.data.productDesc || ""
        }`;
      },
      cellRenderer: (params) => (
        <Tooltip
          title={`${params.data.productId}-${params.data.productDesc}`}
          arrow
        >
          <div>{`${params.data.productId}-${params.data.productDesc}`}</div>
        </Tooltip>
      ),
    },
    {
      field: "fiscalPeriod",
      headerName: "Fiscal Period",
      sortable: true,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      unSortIcon: true,
      width: 180,
    },
    {
      field: "quantity",
      headerName: "Quantity",
      sortable: true,
      filterParams: {
        filterOptions: ["equals", "inRange"],
        defaultOption: "equals",
        buttons: ["apply", "reset"],
      },
      filter: "agNumberColumnFilter",
      unSortIcon: true,
      width: 160,
    },
    {
      field: "status",
      headerName: "Status",
      sortable: true,
      unSortIcon: true,
      width: 150,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      cellRenderer: (params) => <StatusChip status={params.value} />,
    },
    {
      field: "remarks",
      headerName: "Comments",
      sortable: true,
      unSortIcon: true,
      resizable: false,
      width: 330,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      cellRenderer: (params) => (
        <Tooltip title={params.value} arrow>
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "500px",
            }}
          >
            {params.value}
          </div>
        </Tooltip>
      ),
    },
  ];

  const handleSelectionChanged = (event) => {
    const selectedRows = event.api.getSelectedRows();
    const combinationDetails = selectedRows.map(
      ({ bottler, locationName, customerName }) => ({
        bottler,
        locationName,
        customerName,
      }),
    );

    setCard2CombinationDetails(combinationDetails);
  };

  const onModalGridExport = async () => {
    if (!selectedRowData) {
      MessagePopup({
        icon: "error",
        title: "Error!",
        message: "No row selected for export!",
      });
      return;
    }

    setExportLoading(true); // Start the export loader

    const params = {
      status: showSuccessOnly ? "SUCCESS" : showFailedOnly ? "ERROR" : "BOTH",
      locationId: selectedRowData.locationName.split("-")[0],
      customerId: selectedRowData.customerName.split("-")[0],
      submissionId: selectedRowData?.submissionId,
    };

    try {
      const response = await ApiService.downloadFile(
        "/v1/download-forecast-data",
        null,
        null,
        params,
      );

      if (response.status === 200 && response.data) {
        // Create an object URL for the Blob
        const url = window.URL.createObjectURL(response.data);

        // Generate a timestamp for the filename (optional)
        const timestamp = new Date().toISOString().replace(/[-T:.Z]/g, "_");

        // Set the filename for the download
        const filename = `Forecast_report_${selectedRowData?.fiscalMonth}_${selectedRowData?.forecastType}_${timestamp}1.xlsx`;

        // Create a temporary link to trigger the download
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);

        // Append the link to the document body, click it, then remove it
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the object URL to release memory
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Error during export:", error);
      MessagePopup({
        icon: "error",
        title: "Error!",
        message:
          error.response?.status === 404
            ? "Requested resource not found."
            : "An error occurred while exporting data.",
      });
    } finally {
      setExportLoading(false); // Stop the export loader
    }
  };

  const handleOpen = async (rowData) => {
    try {
      setModalLoading(true); // Start loading
      setShowFailedOnly(false);
      setShowSuccessOnly(false);

      const fileResponse = await ApiService.get(
        `/v1/get-file-line-records?submissionId=${
          rowData.submissionId
        }&customerId=${rowData.customerName.split("-")[0]}&locationId=${
          rowData.locationName.split("-")[0]
        }`,
      );

      console.log("Fetched file data:", fileResponse.data);
      setModalRowData(fileResponse.data);
      setSelectedRowData(rowData); // Set the selected row data
      setPopupCombinationDetails([
        {
          bottler: rowData.bottler,
          locationName: rowData.locationName,
          customerName: rowData.customerName,
        },
      ]);
      setOpen(true);
    } catch (error) {
      console.error("Error fetching file", error);
    } finally {
      setModalLoading(false); // Stop loading
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedRowData(null);
  };

  const handleFialureChange = (event) => {
    setShowFailedOnly(event.target.checked);
    if (event.target.checked) {
      setShowSuccessOnly(false);
    }
  };

  const handleSuccessChange = (event) => {
    setShowSuccessOnly(event.target.checked);
    if (event.target.checked) {
      setShowFailedOnly(false);
    }
  };

  const onGridReady = (params) => {
    params.api.selectAll();
  };

  const failedData = modalRowData.filter((row) => row.status === "ERROR");
  const successData = modalRowData.filter((row) => row.status === "SUCCESS");
  const dataToShow = showFailedOnly
    ? failedData
    : showSuccessOnly
      ? successData
      : modalRowData;

  const pagination = true;
  const paginationPageSize = 10;
  const paginationPageSizeSelector = [5, 10, 20, 50, 100];

  const paginationModal = true;
  const paginationModalPageSize = 10;
  const paginationModalPageSizeSelector = [10, 50, 100, 500, 1000];

  const dynamicHeight = useDynamicHeight(
    dataToShow,
    paginationModalPageSize,
    400,
  );

  return (
    <React.Fragment>
      <div className="container">
        <Grid xs={12}>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={8} sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="subtitle1" align="left" gutterBottom="true">
                Concentrate Previous Forecast Uploads
              </Typography>
              <InfoPopover margin="0 0 3px 0" />
            </Grid>

            <Grid
              item
              xs={4}
              sx={{ display: "flex", justifyContent: "flex-end" }}
            >
              <Typography variant="subtitle1" align="right">
                <Typography variant="subtitle1" align="right">
                  <Link
                    to="/bf-history"
                    style={{ color: "grey", textDecoration: "underline" }}
                  >
                    View All History
                  </Link>
                </Typography>
              </Typography>
              <Tooltip title="Refresh" arrow placement="top">
                <RefreshIcon
                  sx={{
                    marginLeft: "10px",
                    cursor: "pointer",
                    color: "grey",
                    "&:hover": {
                      color: "gray",
                    },
                  }}
                  onClick={() => {
                    dispatch(triggerRefresh());
                  }}
                />
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
        <Grid xs={12}>
          {loading ? (
            <Skeleton
              variant="rectangular"
              height={280}
              animation="pulse"
              style={{ marginBottom: "16px", borderRadius: "8px" }}
            />
          ) : (
            <DataTable
              ref={GridRef}
              columns={columnDefinition}
              rows={filteredData || []}
              pagination={pagination}
              paginationPageSize={paginationPageSize}
              paginationPageSizeSelector={paginationPageSizeSelector}
              tableHeight={300}
              suppressMovableColumns={true}
              suppressHorizontalScroll={false}
              suppressPaginationPanel={false}
              onSelectionChanged={handleSelectionChanged}
            />
          )}
          <Modal
            open={open}
            handleClose={handleClose}
            maxWidth={"100%"}
            height={450}
            title="Record Details"
            content={
              modalLoading ? (
                <Skeleton
                  variant="rectangular"
                  height={400}
                  animation="pulse"
                  style={{ marginBottom: "16px", borderRadius: "8px" }}
                />
              ) : (
                <div>
                  {/* TextField Section */}
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "16px",
                      maxWidth: "1100px",
                      marginBottom: "5px",
                    }}
                  >
                    {/* Parent Bottler */}
                    <div style={{ flex: "1 1 20%" }}>
                      <TextField
                        id="ParentBottler"
                        label="Parent Bottler"
                        value={selectedRowData?.bottler || ""}
                        InputProps={{
                          style: {
                            fontSize: "13px",
                            height: "30px",
                            cursor: "default",
                          },
                        }}
                        InputLabelProps={{
                          shrink: true,
                          style: {
                            transform: "translate(14px, -14px) scale(0.75)",
                          },
                        }}
                        variant="outlined"
                        fullWidth
                        size="small"
                      />
                    </div>

                    {/* Forecast Partner */}
                    <div style={{ flex: "1 1 60%" }}>
                      <TextField
                        id="forecast-partner"
                        label="Forecast Partner"
                        value={selectedRowData?.customerName || ""}
                        InputProps={{
                          style: {
                            fontSize: "13px",
                            height: "30px",
                            cursor: "default",
                          },
                        }}
                        InputLabelProps={{
                          shrink: true,
                          style: {
                            transform: "translate(14px, -14px) scale(0.75)",
                          },
                        }}
                        variant="outlined"
                        fullWidth
                        size="small"
                      />
                    </div>

                    {/* Plant */}
                    <div style={{ flex: "1 1 20%" }}>
                      <TextField
                        id="plant"
                        label="Plant"
                        value={selectedRowData?.locationName || ""}
                        InputProps={{
                          style: {
                            fontSize: "13px",
                            height: "30px",
                            cursor: "default",
                          },
                        }}
                        InputLabelProps={{
                          shrink: true,
                          style: {
                            transform: "translate(14px, -14px) scale(0.75)",
                          },
                        }}
                        variant="outlined"
                        fullWidth
                        size="small"
                      />
                    </div>

                    {/* Fiscal Month */}
                    <div style={{ flex: "1 1 20%" }}>
                      <TextField
                        id="fiscalMonth"
                        label="Fiscal Period"
                        value={selectedRowData?.fiscalMonth || ""}
                        InputProps={{
                          style: {
                            fontSize: "13px",
                            height: "30px",
                            cursor: "default",
                          },
                        }}
                        InputLabelProps={{
                          shrink: true,
                          style: {
                            transform: "translate(14px, -14px) scale(0.75)",
                          },
                        }}
                        variant="outlined"
                        fullWidth
                        size="small"
                      />
                    </div>

                    {/* Forecast Term */}
                    <div style={{ flex: "1 1 20%" }}>
                      <TextField
                        id="forecastType"
                        label="Forecast Term"
                        value={selectedRowData?.forecastType || ""}
                        InputProps={{
                          style: {
                            fontSize: "13px",
                            height: "30px",
                            cursor: "default",
                          },
                        }}
                        InputLabelProps={{
                          shrink: true,
                          style: {
                            transform: "translate(14px, -14px) scale(0.75)",
                          },
                        }}
                        variant="outlined"
                        fullWidth
                        size="small"
                      />
                    </div>

                    {/* Upload Date Time (UTC) */}
                    <div style={{ flex: "1 1 20%" }}>
                      <TextField
                        id="createdAt"
                        label="Upload Date Time (UTC)"
                        value={
                          selectedRowData?.createdAt
                            ? Timestamp(selectedRowData.createdAt)
                            : ""
                        }
                        InputProps={{
                          style: {
                            fontSize: "13px",
                            height: "30px",
                            cursor: "default",
                          },
                        }}
                        InputLabelProps={{
                          shrink: true,
                          style: {
                            transform: "translate(14px, -14px) scale(0.75)",
                          },
                        }}
                        variant="outlined"
                        fullWidth
                        size="small"
                      />
                    </div>
                  </div>

                  {/* Filters Section */}
                  <div className="filter-container">
                    <div className="span-container">
                      <span className="button-container">Filters</span>
                      <div className="filter-button">
                        <FormGroup>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={showFailedOnly}
                                onChange={handleFialureChange}
                              />
                            }
                            label="Failed rows"
                            style={{ marginRight: 0 }}
                            labelPlacement="start"
                            componentsProps={{
                              typography: {
                                style: {
                                  marginRight: "auto",
                                  fontSize: "13px",
                                },
                              },
                            }}
                          />
                        </FormGroup>
                      </div>
                      <div className="filter-button">
                        <FormGroup>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={showSuccessOnly}
                                onChange={handleSuccessChange}
                              />
                            }
                            label="Successful rows"
                            style={{ marginRight: 0 }}
                            labelPlacement="start"
                            componentsProps={{
                              typography: {
                                style: {
                                  marginRight: "auto",
                                  fontSize: "13px",
                                },
                              },
                            }}
                          />
                        </FormGroup>
                      </div>
                    </div>

                    {/* Download Button */}
                    <button
                      className="download-button"
                      onClick={onModalGridExport}
                      disabled={exportLoading} // Disable the button while loading
                    >
                      {exportLoading ? "Downloading..." : "Download"}{" "}
                      <CloudDownloadOutlinedIcon className="download-icon" />
                    </button>
                  </div>

                  {/* DataTable Section */}
                  <div className="ag-theme-alpine" style={{ width: "100%" }}>
                    <DataTable
                      ref={modalGridRef}
                      columns={modalDefinition}
                      rows={dataToShow}
                      pagination={paginationModal}
                      paginationPageSize={paginationModalPageSize}
                      paginationPageSizeSelector={
                        paginationModalPageSizeSelector
                      }
                      tableHeight={dynamicHeight}
                      tablewidth={1100}
                      suppressMovableColumns={true}
                      suppressPaginationPanel={false}
                      suppressRowClickSelection={true}
                      suppressCsvExport={false}
                      onGridReady={onGridReady}
                    />
                  </div>
                </div>
              )
            }
          />
        </Grid>
      </div>
    </React.Fragment>
  );
}

export default PastForeCastTracker;
